import { useCallback, useRef } from 'react';

/**
 * Which box runs off the edge with a table's scroller.
 *
 * - `'self'`: the scroller draws the box itself (a bordered, rounded `overflow-x-auto` div).
 * - `'parent'`: the scroller's parent, a card that holds the table and nothing else (what `Table`
 *   is drawn in).
 * - `'marked'`: the nearest ancestor carrying {@link EDGE_BLEED_FRAME}, a card that holds the table
 *   among other things (a heading, a note, a Save button). The whole card extends, its content with
 *   it, so the card's own right border never cuts across the table.
 *
 * Whatever is named, it must sit inside the page's content column: a frame that would be the column
 * itself, or anything around it, falls back to the scroller.
 */
export type EdgeBleedFrame = 'self' | 'parent' | 'marked';

/** Spread onto the card a table sits in (`<Card {...EDGE_BLEED_FRAME}>`) for `frame: 'marked'`. */
export const EDGE_BLEED_FRAME = { 'data-edge-bleed-frame': '' } as const;

/** Tailwind's `lg`: below it the page is a tablet or phone layout, which keeps its own edges. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/** The page's scroll region (StandardLayout). The box runs to its right edge, never past it. */
const PAGE_SCROLLER = '[data-page-scroller]';

/** The inline properties this hook writes, and so owns, on the boxes it extends. */
const OWNED = [
  'margin-right',
  'border-right-width',
  'border-top-right-radius',
  'border-bottom-right-radius',
] as const;

/** The parts of a MediaQueryList used here. Safari before 14 has only addListener / removeListener. */
interface DesktopQuery {
  readonly matches: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
}

function openDesktopQuery(): DesktopQuery | null {
  try {
    return typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null;
  } catch {
    return null;
  }
}

function frameOf(scroller: HTMLElement, frame: EdgeBleedFrame, page: HTMLElement): HTMLElement {
  const named =
    frame === 'parent'
      ? scroller.parentElement
      : frame === 'marked'
        ? scroller.parentElement?.closest<HTMLElement>('[data-edge-bleed-frame]') ?? null
        : null;
  // The page's direct child is its content column: extending that would move every card on the page.
  const inside = named && named !== page && named.parentElement !== page && page.contains(named);
  return inside ? named : scroller;
}

/**
 * A wide table runs off the right edge of a desktop page, so the cut at the window's edge says "this
 * scrolls sideways". A box that ends inside the page margin, with the table clipped in it, reads as
 * a table that simply ends there.
 *
 * Returns a callback ref for the table's scroller (its `overflow-x-auto` box). While the screen is at
 * least `lg` (1024px) AND the table needs more width than the page gives it, the box that frames it
 * ({@link EdgeBleedFrame}) reaches to the right edge of the page's scroll region, just left of the
 * page's vertical scrollbar, and drops its right border and right rounded corners. Its left edge
 * stays with the page content. Below `lg`, or while the table fits, nothing is changed.
 *
 * Measured, never assumed: the page is centred in a 1440px column, and its sizes are rem-based, so the
 * distance to the edge depends on the window and on the root font size. The box is extended by
 * whole pixels, rounded down, so it can never reach past the edge and give the page a sideways
 * scrollbar of its own.
 *
 * Every decision is taken on the box as it is WITHOUT the bleed: the hook takes its own styles off,
 * asks the browser whether the table overflows its scroller (an auto-layout `w-full` table is as wide
 * as its scroller or its own minimum width, whichever is more, so `scrollWidth > clientWidth` is
 * exactly "its minimum width is more than the page gives it"), then measures the gap and puts the
 * bleed back - in one task, so nothing is painted in between. A table that fits once its box is wider
 * would otherwise un-bleed, overflow again and bleed again, every frame.
 *
 * Measured again when the window, the page's scroll region, the box or the table changes size, when
 * the table's content changes, and when the screen crosses `lg`. A window resize is answered in the
 * `resize` event itself, which runs before that frame's layout, so a shrinking window never paints a
 * frame with the old, now too wide, bleed. Anything else is answered in the next animation frame,
 * never inside the ResizeObserver callback: a size written there raises the browser's
 * "ResizeObserver loop" error.
 *
 * Outside a `[data-page-scroller]` (a modal, a page with its own panes), with no ResizeObserver or no
 * matchMedia, nothing is set. The hook owns the margin-right, border-right-width and right border
 * radii inline styles of the boxes it extends, so their own markup must not set those inline.
 *
 * The ref holds nothing in React state, so attaching it renders nothing. React hands it the scroller
 * in the commit that puts the table on screen, before that commit is painted, and it starts there; a
 * ref that kept the element in state rendered its host a second time on every mount - PostsTable,
 * every row of it. It is the same function on every render while `frame` stays the same, so React
 * calls it again only when the table goes (with null: everything stops and every style it set comes
 * off) or when `frame` changes (the old ref with null, then the new one with the scroller).
 */
export function useEdgeBleed<T extends HTMLElement>(
  frame: EdgeBleedFrame = 'self',
): (scroller: T | null) => void {
  // What stops the bleed running for the element the ref holds now; null while none runs.
  const stop = useRef<(() => void) | null>(null);
  return useCallback(
    (scroller: T | null) => {
      stop.current?.();
      stop.current = scroller ? bleedToEdge(scroller, frame) : null;
    },
    [frame],
  );
}

/**
 * Runs `scroller`'s frame to the page's right edge whenever it should, from now until the returned
 * function is called: it measures straight away, then again whenever something it depends on
 * changes. The returned function stops all of that and takes off every style it set. Null, with
 * nothing set, outside a page scroll region or without ResizeObserver or matchMedia.
 */
function bleedToEdge(scroller: HTMLElement, frame: EdgeBleedFrame): (() => void) | null {
  if (typeof ResizeObserver === 'undefined') return null;
  const page = scroller.closest<HTMLElement>(PAGE_SCROLLER);
  if (!page) return null;
  const desktop = openDesktopQuery();
  if (!desktop) return null;
  const box = frameOf(scroller, frame, page);
  const boxes = box === scroller ? [box] : [box, scroller];

  const clear = () => {
    for (const el of boxes) for (const property of OWNED) el.style.removeProperty(property);
  };
  const extend = (el: HTMLElement, gap: number) => {
    if (gap > 0) el.style.setProperty('margin-right', `${-gap}px`);
    el.style.setProperty('border-right-width', '0px');
    el.style.setProperty('border-top-right-radius', '0px');
    el.style.setProperty('border-bottom-right-radius', '0px');
  };

  const measure = () => {
    clear();
    if (!desktop.matches) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return;
    // The inside of the page's scroll region's border: its right edge sits just left of the page's
    // scrollbar. The region always draws that scrollbar's track (StandardLayout), so a box run to
    // here meets the scrollbar at the window's edge, on a page too short to scroll as well.
    const edge = page.getBoundingClientRect().left + page.clientLeft + page.clientWidth;
    const gap = Math.floor(edge - box.getBoundingClientRect().right);
    if (gap < 1) return;
    extend(box, gap);
    // A scroller inside a padded card is pulled through the card's padding to the same edge,
    // measured once the card has moved.
    if (box !== scroller) extend(scroller, Math.floor(edge - scroller.getBoundingClientRect().right));
  };
  measure();

  let pending: number | null = null;
  const later = () => {
    if (pending != null) return;
    pending = requestAnimationFrame(() => {
      pending = null;
      measure();
    });
  };
  const now = () => {
    if (pending != null) cancelAnimationFrame(pending);
    pending = null;
    measure();
  };

  const observer = new ResizeObserver(later);
  observer.observe(page);
  for (const el of boxes) observer.observe(el);
  const table = scroller.firstElementChild;
  if (table) observer.observe(table);
  // A new list in the same table (PostsTable's Status tabs) can change the table's minimum width
  // without changing any size the ResizeObserver sees - as many rows, the old list no wider than
  // the extended box, the new one fitting - so decide again when its content changes too.
  // Attributes are left out: the hook writes only to the boxes around the table, and picking a
  // row only changes its classes.
  const content = typeof MutationObserver === 'undefined' ? null : new MutationObserver(later);
  if (table && content) content.observe(table, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', now);
  if (desktop.addEventListener) desktop.addEventListener('change', now);
  else desktop.addListener?.(now);

  return () => {
    observer.disconnect();
    content?.disconnect();
    window.removeEventListener('resize', now);
    if (desktop.removeEventListener) desktop.removeEventListener('change', now);
    else desktop.removeListener?.(now);
    if (pending != null) cancelAnimationFrame(pending);
    clear();
  };
}
